/**
 * Hook to fetch global portal settings
 * Uses type-safe transformers to convert DB data to app types
 */

import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase/client';
import { transformPortalSettings } from '../../../lib/supabase/index';
import type { PortalSettings } from '../../../lib/supabase/transforms';

// Re-export type for component use
export type { PortalSettings };

interface UsePortalSettingsReturn {
  settings: PortalSettings | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// Default settings used when none exist in DB
const DEFAULT_SETTINGS: PortalSettings = {
  id: 'default',
  founderImageUrl: '',
  founderName: 'Turn Literacy',
  defaultIntroMessage: 'Welcome to your reading journey!',
  defaultVoiceId: 'ErXwobaYiN019PkySvjV', // ElevenLabs Antoni
};

/**
 * Fetch global portal settings
 */
export function usePortalSettings(): UsePortalSettingsReturn {
  const [settings, setSettings] = useState<PortalSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('portal_settings')
        .select('*')
        .limit(1)
        .maybeSingle();

      if (fetchError) {
        throw fetchError;
      } else if (data) {
        setSettings(transformPortalSettings(data));
      } else {
        // No settings found - use defaults
        setSettings(DEFAULT_SETTINGS);
      }
    } catch (err) {
      console.error('Error fetching portal settings:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch settings');
      // Use defaults on error
      setSettings(DEFAULT_SETTINGS);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  return { settings, loading, error, refetch: fetchSettings };
}
